import React, { useEffect, useMemo, useState } from 'react';
import type { TackleBoxItem, ToastType, UserProfile } from '../types';
import { updateGearItem, uploadGearImage } from '../services/gear';
import { getRoddoUiTokens, resolveRoddoDarkMode } from '../constants/theme';

type Props = {
  open: boolean;
  item: TackleBoxItem | null;
  userProfile: UserProfile | null;
  onClose: () => void;
  onSaved?: () => void;
  addToast?: (message: string, type?: ToastType) => void;
};

const EditGearModal: React.FC<Props> = ({
  open,
  item,
  userProfile,
  onClose,
  onSaved,
  addToast,
}) => {
  const isDark = resolveRoddoDarkMode();
  const ui = getRoddoUiTokens(isDark) as any;

  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [notes, setNotes] = useState('');
  const [weightOz, setWeightOz] = useState('');
  const [value, setValue] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [saving, setSaving] = useState(false);

  const safeToast = (message: string, type: ToastType = 'info') => {
    if (addToast) addToast(message, type);
    else console.log(`[EditGearModal:${type}] ${message}`);
  };

  useEffect(() => {
    if (!open || !item) return;
    setName(String(item.name || ''));
    setBrand(String(item.brand || ''));
    setModel(String(item.model || ''));
    setNotes(String(item.notes || ''));
    setWeightOz(typeof item.weightOz === 'number' ? String(item.weightOz) : '');
    setValue(typeof item.value === 'number' ? String(item.value) : '');
    setPhotoFile(null);
    setRemovePhoto(false);
    setPhotoPreviewUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return item.imageUrl || null;
    });
  }, [open, item]);

  useEffect(() => {
    return () => {
      if (photoPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(photoPreviewUrl);
    };
  }, [photoPreviewUrl]);

  const parsedWeightOz = useMemo(() => {
    if (!weightOz.trim()) return undefined;
    const n = Number(weightOz);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  }, [weightOz]);

  const parsedValue = useMemo(() => {
    if (!value.trim()) return undefined;
    const n = Number(value);
    return Number.isFinite(n) && n >= 0 ? n : undefined;
  }, [value]);

  if (!open || !item) return null;

  const choosePhoto = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      safeToast('Please choose an image file.', 'error');
      return;
    }

    const nextUrl = URL.createObjectURL(file);
    setPhotoPreviewUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return nextUrl;
    });
    setPhotoFile(file);
    setRemovePhoto(false);
  };

  const clearPhoto = () => {
    setPhotoFile(null);
    setRemovePhoto(true);
    setPhotoPreviewUrl((prev) => {
      if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const save = async () => {
    if (!userProfile?.uid || !item.id || saving) return;
    const cleanName = name.trim();
    if (!cleanName) {
      safeToast('Please give your gear a name.', 'error');
      return;
    }

    setSaving(true);
    try {
      let imageUrl: string | null | undefined = item.imageUrl || undefined;
      if (removePhoto) imageUrl = null;
      if (photoFile) imageUrl = await uploadGearImage(userProfile.uid, item.id, photoFile);

      await updateGearItem(userProfile.uid, item.id, {
        name: cleanName,
        brand: brand.trim() || undefined,
        model: model.trim() || undefined,
        notes: notes.trim() || undefined,
        weightOz: parsedWeightOz,
        value: parsedValue,
        imageUrl,
      } as Partial<TackleBoxItem>);

      safeToast('Gear updated.', 'success');
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('[EditGearModal] update failed', error);
      safeToast('Could not update this item. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const shellStyle: React.CSSProperties = {
    background: ui.cardBgStrong ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    boxShadow: isDark
      ? '0 18px 44px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.05)'
      : '0 18px 42px rgba(89,65,37,.14), inset 0 1px 0 rgba(255,255,255,.72)',
    color: ui.textPrimary,
  };
  const sectionStyle: React.CSSProperties = {
    background: ui.cardBgMuted ?? ui.surfaceBg ?? ui.cardBg,
    border: `1px solid ${ui.border}`,
  };
  const inputStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.inputBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    color: ui.textPrimary,
    boxShadow: `${ui.insetHighlight}, inset 0 1px 0 rgba(255,255,255,.10)`,
  };
  const secondaryStyle: React.CSSProperties = {
    background: ui.surfaceBg ?? ui.cardBgMuted ?? ui.cardBg,
    border: `1px solid ${ui.borderStrong ?? ui.border}`,
    color: ui.textPrimary,
    boxShadow: `${ui.shadow}, inset 0 1px 0 rgba(255,255,255,.10)`,
  };
  const saveStyle: React.CSSProperties = {
    background: isDark
      ? 'linear-gradient(180deg,#67D8E8 0%,#32B7CC 100%)'
      : 'linear-gradient(180deg,#5B8DFF 0%,#2B6CFF 100%)',
    color: '#fff',
    boxShadow: isDark ? '0 8px 18px rgba(50,183,204,.20)' : '0 8px 18px rgba(43,108,255,.20)',
  };
  const fieldClass = 'roddo-mobile-form w-full rounded-[13px] px-3.5 py-2.5 text-sm outline-none';

  return (
    <div
      className="fixed inset-x-0 z-[1001] overflow-hidden bg-black/45 backdrop-blur-sm"
      style={{
        top: 'calc(var(--roddo-header-h, 0px) + 8px)',
        bottom: 'calc(var(--roddo-nav-h, 76px) + 8px)',
      }}
      onClick={() => { if (!saving) onClose(); }}
    >
      <div
        className="mx-auto h-full min-h-0 w-full max-w-md px-2 py-2"
        style={{
          paddingLeft: 'max(8px, env(safe-area-inset-left))',
          paddingRight: 'max(8px, env(safe-area-inset-right))',
        }}
      >
        <div className="relative h-full overflow-hidden rounded-[22px]" style={shellStyle}>
          <div className="flex h-full min-h-0 flex-col" onClick={(event) => event.stopPropagation()}>
            <div className="shrink-0 border-b px-5 pb-4 pt-5" style={{ borderColor: ui.borderStrong ?? ui.border }}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-[10px] font-black uppercase tracking-[.22em]" style={{ color: ui.textSecondary }}>
                    The Hold · {String(item.category || 'gear')}
                  </div>
                  <h2 className="mt-1 text-[23px] font-black leading-tight">Edit gear</h2>
                  <p className="mt-1.5 text-xs font-semibold" style={{ color: ui.textSecondary }}>
                    Update the details anglers and Roddo use for this setup.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Close edit gear"
                  disabled={saving}
                  onClick={onClose}
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-lg font-black transition active:scale-95 disabled:opacity-50"
                  style={secondaryStyle}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 [-webkit-overflow-scrolling:touch]">
              <div className="rounded-[18px] p-4" style={sectionStyle}>
                <div className="space-y-4">
                  <div>
                    <label className="mb-2 block text-[10px] font-black uppercase tracking-[.18em]" style={{ color: ui.textSecondary }}>Gear name</label>
                    <input className={fieldClass} style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-2 block text-[10px] font-black uppercase tracking-[.18em]" style={{ color: ui.textSecondary }}>Brand</label>
                      <input className={fieldClass} style={inputStyle} value={brand} onChange={(e) => setBrand(e.target.value)} />
                    </div>
                    <div>
                      <label className="mb-2 block text-[10px] font-black uppercase tracking-[.18em]" style={{ color: ui.textSecondary }}>Model</label>
                      <input className={fieldClass} style={inputStyle} value={model} onChange={(e) => setModel(e.target.value)} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-2 block text-[10px] font-black uppercase tracking-[.18em]" style={{ color: ui.textSecondary }}>Weight (oz)</label>
                      <input type="number" min="0" step="0.01" inputMode="decimal" className={fieldClass} style={inputStyle} value={weightOz} onChange={(e) => setWeightOz(e.target.value)} />
                    </div>
                    <div>
                      <label className="mb-2 block text-[10px] font-black uppercase tracking-[.18em]" style={{ color: ui.textSecondary }}>Value</label>
                      <input type="number" min="0" step="0.01" inputMode="decimal" className={fieldClass} style={inputStyle} value={value} onChange={(e) => setValue(e.target.value)} />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-[10px] font-black uppercase tracking-[.18em]" style={{ color: ui.textSecondary }}>Notes</label>
                    <textarea rows={4} className={fieldClass} style={inputStyle} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Setup, technique, line pairing, or anything worth remembering." />
                  </div>

                  <div>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <label className="block text-[10px] font-black uppercase tracking-[.18em]" style={{ color: ui.textSecondary }}>Gear photo</label>
                      {photoPreviewUrl ? (
                        <button type="button" onClick={clearPhoto} disabled={saving} className="text-[10px] font-black text-rose-500 transition active:scale-95 disabled:opacity-50">Remove</button>
                      ) : null}
                    </div>

                    {photoPreviewUrl ? (
                      <div className="mb-3 overflow-hidden rounded-[16px]" style={{ border: `1px solid ${ui.border}` }}>
                        <img src={photoPreviewUrl} alt="Gear preview" className="h-40 w-full object-cover" />
                      </div>
                    ) : null}

                    <label className="flex min-h-[76px] cursor-pointer items-center justify-center rounded-[14px] border border-dashed px-4 text-center transition active:scale-[.99]" style={{ ...secondaryStyle, borderStyle: 'dashed' }}>
                      <input type="file" accept="image/*" className="hidden" disabled={saving} onChange={choosePhoto} />
                      <span>
                        <span className="block text-sm font-black">{photoPreviewUrl ? 'Change photo' : 'Add photo'}</span>
                        <span className="mt-1 block text-[10px] font-semibold" style={{ color: ui.textSecondary }}>Choose a new reference shot for this setup.</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="shrink-0 border-t px-5 py-3" style={{ borderColor: ui.borderStrong ?? ui.border, background: ui.cardBgStrong ?? ui.cardBg }}>
              <div className="flex items-center gap-3">
                <button type="button" disabled={saving} onClick={onClose} className="flex-1 rounded-[13px] px-4 py-2.5 text-sm font-bold transition active:scale-[.98] disabled:opacity-50" style={secondaryStyle}>Cancel</button>
                <button type="button" disabled={saving} onClick={() => void save()} className="flex-1 rounded-[13px] px-4 py-2.5 text-sm font-black transition active:scale-[.98] disabled:opacity-60" style={saveStyle}>
                  {saving ? 'Saving…' : 'Save changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditGearModal;
